//-- copyright
// OpenProject Resource Planner (multi-resource) management plugin.
//++

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit, inject,
} from '@angular/core';
import {
  parseISO, isoDate, addDays, isWorkingDay, isHoliday, workingHoursOn,
  TpCalendar, TpPeriod, CalLookup,
} from '../shared/timephased.util';

interface UCal { id:number; name:string; isDefault:boolean; definition:TpCalendar; }
interface URes {
  id:number; name:string; initials:string|null; resourceType:string|null; maxUnits:number;
  calendarId:number|null; availabilities:{ from:string|null; to:string|null; maxUnits:number }[];
}
interface UAssignment { resourceId:number; units:number; start:string|null; finish:string|null; timephased:TpPeriod[]; }
interface UTask { id:number; name:string; start:string|null; finish:string|null; assignments:UAssignment[]; }
interface Section { group:string; items:URes[]; }
interface Stat { daily:Record<string,number>; cap:Record<string,number>; working:Record<string,boolean>; holiday:Record<string,boolean>; peak:number; avg:number; overDays:number; cal:UCal|null; }

// ── precomputed view model (built once per interaction, read as plain data) ──
// The template binds ONLY to these primitive fields — no method calls — so a
// change-detection pass does no work beyond dirty-checking strings/booleans.
interface VCell { cls:string; title:string; text:string; }
interface VSubCell { cls:string; text:string; }
interface VSubRow { taskId:number; taskName:string; link:string; linkTitle:string; day:VSubCell[]; week:VSubCell[]; }
interface VRow {
  id:number; name:string; initials:string|null; typeColor:string;
  cap:number; calName:string; peak:number; chipClass:string; expanded:boolean;
  day:VCell[]; week:VCell[]; subRows:VSubRow[];
}
interface VSection { group:string; rows:VRow[]; }
interface VHeader { dow:string; dnum:number; wkStart:boolean; weekend:boolean; today:boolean; }
interface VWeekHeader { label:string; }
interface VSummary { resCount:number; taskCount:number; overCount:number; overDanger:boolean; peakPct:number; peakDanger:boolean; avgPct:number; }

@Component({
  selector: 'rp-resource-usage',
  templateUrl: './resource-usage.component.html',
  styleUrls: ['./resource-usage.component.sass'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourceUsageComponent implements OnInit {
  @Input() projectId = '';
  @Input() projectIdentifier = '';
  @Input() projectName = '';

  private cdr = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);

  readonly WDOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  readonly MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  readonly TYPE_COLOR:Record<string,string> = { work: '#0969da', equipment: '#8250df', material: '#bf3989', cost: '#1a7f37' };
  readonly weeksShown = 5;

  loading = true;
  error:string|null = null;
  calendars:UCal[] = [];
  sections:Section[] = [];
  tasks:UTask[] = [];

  startISO = '';
  defaultStartISO = '';
  todayISO = isoDate(new Date());
  mode:'day'|'week' = 'day';
  overOnly = false;
  private expanded = new Set<number>();

  private days:Date[] = [];
  private weeks:Date[][] = [];
  private stats:Record<number, Stat> = {};
  // resourceId → its assignments, indexed once (was an O(tasks×assignments) rescan per call).
  private byResource = new Map<number, { a:UAssignment; task:UTask }[]>();

  // ── view model consumed by the template ─────────────────────────────
  viewSections:VSection[] = [];
  dayHeaders:VHeader[] = [];
  weekHeaders:VWeekHeader[] = [];
  summary:VSummary = { resCount: 0, taskCount: 0, overCount: 0, overDanger: false, peakPct: 0, peakDanger: false, avgPct: 0 };
  winLabel = '';
  colCount = 0;
  isEmpty = false;

  trackSection = (_:number, s:VSection):string => s.group;
  trackRow = (_:number, r:VRow):number => r.id;
  trackSub = (_:number, s:VSubRow):number => s.taskId;
  trackIndex = (i:number):number => i;

  ngOnInit():void {
    if (!this.projectId) { this.projectId = this.elementRef.nativeElement.getAttribute('project-id') || ''; }
    if (!this.projectIdentifier) { this.projectIdentifier = this.elementRef.nativeElement.getAttribute('project-identifier') || ''; }
    this.load();
  }

  private load():void {
    this.loading = true;
    fetch(`/projects/${this.projectId}/resource_planner/api/usage`,
      { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        this.calendars = d.calendars || [];
        this.sections = d.resources || [];
        this.tasks = d.tasks || [];
        this.startISO = d.window?.start || isoDate(new Date());
        this.defaultStartISO = this.startISO;
        this.indexAssignments();
        this.rebuild();
        this.loading = false; this.cdr.markForCheck();
      }).catch(() => { this.error = 'Failed to load resource usage.'; this.loading = false; this.cdr.markForCheck(); });
  }

  // Build resourceId → assignments once, instead of scanning every task on demand.
  private indexAssignments():void {
    this.byResource.clear();
    this.tasks.forEach((t) => t.assignments.forEach((a) => {
      const list = this.byResource.get(a.resourceId);
      if (list) { list.push({ a, task: t }); } else { this.byResource.set(a.resourceId, [{ a, task: t }]); }
    }));
  }

  // ── window + stats ────────────────────────────────────────────────
  // Full recompute: cheap because it runs once per user interaction (load,
  // navigate, mode/filter/expand toggle) — never on a change-detection tick.
  private rebuild():void {
    const start = parseISO(this.startISO);
    this.days = Array.from({ length: this.weeksShown * 7 }, (_, i) => addDays(start, i));
    this.weeks = [];
    for (let i = 0; i < this.days.length; i += 7) { this.weeks.push(this.days.slice(i, i + 7)); }
    this.computeStats();
    this.buildView();
  }

  private calFor(res:URes):UCal|null {
    return this.calendars.find((c) => c.id === res.calendarId)
      || this.calendars.find((c) => c.isDefault) || this.calendars[0] || null;
  }
  // id → definition, for parentCalendarId inheritance in the resolver.
  private lookup:CalLookup = (id:number) => this.calendars.find((c) => c.id === id)?.definition;
  private assignmentsFor(resId:number):{ a:UAssignment; task:UTask }[] {
    return this.byResource.get(resId) || [];
  }
  private maxUnitsOn(res:URes, dateISO:string):number {
    const hit = (res.availabilities || []).find((r) => (!r.from || dateISO >= r.from) && (!r.to || dateISO <= r.to));
    return hit ? hit.maxUnits : res.maxUnits;
  }
  // Allocation % contributed by one assignment on a day. Uses timephased hours
  // (converted to a % of the calendar working day) when present, else flat units.
  private unitsForDay(a:UAssignment, dateISO:string, cal:TpCalendar|null):number {
    if (a.timephased && a.timephased.length) {
      const tp = a.timephased.find((p) => p.date === dateISO);
      if (!tp) { return 0; }
      // Working hours for *this* day (variable per weekday / shift), falling
      // back to the calendar's average day, then 8h.
      const hpd = workingHoursOn(cal, parseISO(dateISO), this.lookup)
        || ((cal && cal.minutesPerDay ? cal.minutesPerDay / 60 : 0)) || 8;
      return hpd > 0 ? Math.round((tp.hours / hpd) * 100) : 0;
    }
    if (a.start && a.finish && dateISO >= a.start && dateISO <= a.finish) { return a.units; }
    return 0;
  }

  private computeStats():void {
    const m:Record<number, Stat> = {};
    this.sections.forEach((sec) => sec.items.forEach((res) => {
      const cal = this.calFor(res);
      const def = cal ? cal.definition : null;
      const entries = this.assignmentsFor(res.id); // hoisted out of the per-day loop
      const daily:Record<string,number> = {}; const cap:Record<string,number> = {};
      const working:Record<string,boolean> = {}; const holiday:Record<string,boolean> = {};
      let peak = 0; let sum = 0; let workDays = 0; let overDays = 0;
      this.days.forEach((dt) => {
        const iso = isoDate(dt);
        const w = isWorkingDay(def, dt, this.lookup);
        working[iso] = w; holiday[iso] = isHoliday(def, dt);
        const c = this.maxUnitsOn(res, iso); cap[iso] = c;
        let load = 0;
        if (w) { entries.forEach(({ a }) => { load += this.unitsForDay(a, iso, def); }); }
        daily[iso] = load;
        if (w) { workDays += 1; sum += load; if (load > peak) { peak = load; } if (c > 0 && load > c) { overDays += 1; } }
      });
      m[res.id] = { daily, cap, working, holiday, peak, avg: workDays ? Math.round(sum / workDays) : 0, overDays, cal };
    }));
    this.stats = m;
  }

  // ── view-model construction ─────────────────────────────────────────
  private buildView():void {
    // headers
    this.dayHeaders = this.days.map((dt) => ({
      dow: this.WDOW[dt.getDay()][0], dnum: dt.getDate(),
      wkStart: dt.getDay() === 1, weekend: dt.getDay() === 0 || dt.getDay() === 6,
      today: isoDate(dt) === this.todayISO,
    }));
    this.weekHeaders = this.weeks.map((wk) => ({ label: this.weekLabel(wk) }));
    this.colCount = this.mode === 'day' ? this.days.length : this.weeks.length;

    let resCount = 0; let overCount = 0; let peakFrac = 0; let avgAccum = 0;

    this.viewSections = this.sections.map((sec) => {
      const rows:VRow[] = [];
      sec.items.forEach((res) => {
        const st = this.stats[res.id];
        const over = (st?.overDays || 0) > 0;
        resCount += 1;
        if (over) { overCount += 1; }
        const cap = this.capForPeak(res);
        const peak = st?.peak || 0;
        if (cap) { peakFrac = Math.max(peakFrac, peak / cap); avgAccum += ((st?.avg || 0) / cap) * 100; }
        if (this.overOnly && !over) { return; }
        rows.push(this.buildRow(res, st, cap, peak));
      });
      return { group: sec.group, rows };
    }).filter((s) => s.rows.length > 0);

    this.summary = {
      resCount, taskCount: this.tasks.length, overCount,
      overDanger: overCount > 0,
      peakPct: Math.round(peakFrac * 100), peakDanger: peakFrac > 1,
      avgPct: resCount ? Math.round(avgAccum / resCount) : 0,
    };

    const s = parseISO(this.startISO); const e = addDays(s, this.weeksShown * 7 - 1);
    this.winLabel = `${s.getDate()} ${this.MON[s.getMonth()]} – ${e.getDate()} ${this.MON[e.getMonth()]} ${e.getFullYear()}`;
    this.isEmpty = resCount === 0;
  }

  private buildRow(res:URes, st:Stat|undefined, cap:number, peak:number):VRow {
    const def = st?.cal?.definition || null;
    const expanded = this.expanded.has(res.id);
    const day:VCell[] = this.days.map((dt) => this.dayCell(st, dt));
    const week:VCell[] = this.weeks.map((wk) => this.weekCell(st, wk));
    const subRows:VSubRow[] = expanded ? this.assignmentsFor(res.id).map((entry) => this.buildSubRow(st, def, entry)) : [];
    return {
      id: res.id, name: res.name, initials: res.initials,
      typeColor: this.TYPE_COLOR[res.resourceType || 'work'] || '#0969da',
      cap, calName: st?.cal ? st.cal.name.split(' (')[0] : '—',
      peak, chipClass: this.heatClass(this.level(peak, cap)) || '-idle',
      expanded, day, week, subRows,
    };
  }

  private dayCell(st:Stat|undefined, dt:Date):VCell {
    const iso = isoDate(dt);
    const working = !!st?.working[iso];
    const holiday = !!st?.holiday[iso];
    const load = st?.daily[iso] || 0;
    const capDay = st?.cap[iso] || 0;
    const parts:string[] = [];
    if (working) { const h = this.heatClass(this.level(load, capDay)); if (h) { parts.push(h); } }
    if (!working && !holiday) { parts.push('nonwork'); }
    if (holiday) { parts.push('holiday'); }
    if (dt.getDay() === 1) { parts.push('wk-start'); }
    if (iso === this.todayISO) { parts.push('today'); }
    return { cls: parts.join(' '), title: this.dayTitle(dt, working, holiday, load, capDay), text: load ? String(load) : '' };
  }

  private weekCell(st:Stat|undefined, wk:Date[]):VCell {
    const working = wk.some((dt) => !!st?.working[isoDate(dt)]);
    const peak = Math.max(0, ...wk.map((dt) => st?.daily[isoDate(dt)] || 0));
    const first = wk.find((dt) => !!st?.working[isoDate(dt)]) || wk[0];
    const capWk = st?.cap[isoDate(first)] || 0;
    const parts:string[] = ['week', 'wk-start'];
    if (working) { const h = this.heatClass(this.level(peak, capWk)); if (h) { parts.push(h); } } else { parts.push('nonwork'); }
    const title = working
      ? `Week of ${this.weekLabel(wk)} · peak ${peak}% of ${capWk}% capacity`
      : `${this.weekLabel(wk)} · Non-working week`;
    return { cls: parts.join(' '), title, text: peak ? String(peak) : '' };
  }

  private buildSubRow(st:Stat|undefined, def:TpCalendar|null, entry:{ a:UAssignment; task:UTask }):VSubRow {
    const day:VSubCell[] = this.days.map((dt) => this.subDayCell(st, def, entry, dt));
    const week:VSubCell[] = this.weeks.map((wk) => this.subWeekCell(st, def, entry, wk));
    return {
      taskId: entry.task.id, taskName: entry.task.name,
      link: this.wpLink(entry.task.id),
      linkTitle: `#${entry.task.id} ${entry.task.name} — open Resources tab`,
      day, week,
    };
  }

  private subDayCell(st:Stat|undefined, def:TpCalendar|null, entry:{ a:UAssignment; task:UTask }, dt:Date):VSubCell {
    const iso = isoDate(dt);
    const working = !!st?.working[iso];
    const holiday = !!st?.holiday[iso];
    const units = working ? this.unitsForDay(entry.a, iso, def) : 0;
    const parts:string[] = [];
    if (!working && !holiday) { parts.push('nonwork'); }
    if (holiday) { parts.push('holiday'); }
    if (dt.getDay() === 1) { parts.push('wk-start'); }
    if (iso === this.todayISO) { parts.push('today'); }
    if (units > 0) { parts.push('sub-active'); }
    return { cls: parts.join(' '), text: units ? String(units) : '' };
  }

  private subWeekCell(st:Stat|undefined, def:TpCalendar|null, entry:{ a:UAssignment; task:UTask }, wk:Date[]):VSubCell {
    const working = wk.some((dt) => !!st?.working[isoDate(dt)]);
    const peak = Math.max(0, ...wk.map((dt) => (st?.working[isoDate(dt)] ? this.unitsForDay(entry.a, isoDate(dt), def) : 0)));
    const parts:string[] = ['week', 'wk-start'];
    if (!working) { parts.push('nonwork'); }
    if (peak > 0) { parts.push('sub-active'); }
    return { cls: parts.join(' '), text: peak ? String(peak) : '' };
  }

  private dayTitle(dt:Date, working:boolean, holiday:boolean, load:number, cap:number):string {
    const label = `${this.WDOW[dt.getDay()]} ${dt.getDate()} ${this.MON[dt.getMonth()]}`;
    if (holiday) { return `${label} · Holiday`; }
    if (!working) { return `${label} · Non-working day`; }
    return `${label} · ${load}% allocated of ${cap}% capacity`;
  }

  // ── navigation (each mutates state, rebuilds view once, marks for check) ──
  shift(weeks:number):void { this.startISO = isoDate(addDays(parseISO(this.startISO), weeks * 7)); this.rebuild(); this.cdr.markForCheck(); }
  resetWindow():void { this.startISO = this.defaultStartISO; this.rebuild(); this.cdr.markForCheck(); }
  setMode(m:'day'|'week'):void { if (this.mode !== m) { this.mode = m; this.colCount = m === 'day' ? this.days.length : this.weeks.length; this.cdr.markForCheck(); } }
  toggleOver():void { this.overOnly = !this.overOnly; this.buildView(); this.cdr.markForCheck(); }
  toggle(id:number):void {
    if (this.expanded.has(id)) { this.expanded.delete(id); } else { this.expanded.add(id); }
    this.buildView(); this.cdr.markForCheck();
  }

  private capForPeak(res:URes):number { return this.maxUnitsOn(res, this.startISO) || res.maxUnits; }

  private weekLabel(wk:Date[]):string { const d = wk[1] || wk[0]; return `${d.getDate()} ${this.MON[wk[0].getMonth()]}`; }

  // Link a task straight to its work package's Resources tab.
  private wpLink(id:number):string {
    const base = this.projectIdentifier ? `/projects/${this.projectIdentifier}` : '';
    return `${base}/work_packages/${id}/resource-planner`;
  }

  // ── heat colouring ────────────────────────────────────────────────
  // Load as a fraction of capacity → a 0–4 intensity level. 0 = idle (no fill).
  private level(load:number, cap:number):number {
    if (load <= 0) { return 0; }
    const r = cap > 0 ? load / cap : 2;
    if (r > 1) { return 4; }
    if (r > 0.85) { return 3; }
    if (r > 0.5) { return 2; }
    return 1;
  }
  private heatClass(lvl:number):string { return lvl ? `rpu-heat-${lvl}` : ''; }
}
